import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import { LoanTerms } from '../../common/types';
import { compareBuyOrRent } from '../../mortrage-lib/rentOrBuy';
import {
  RentOrBuyActions,
  RentOrBuyActionTypes,
  RentOrBuyContextType,
  RentOrBuyState
} from '../types';
import {
  setAnnualAppreciation,
  setClosingCostPercent,
  setCurrentAnnualInterest,
  setDefaults,
  setDownPayment,
  setDownPaymentPercent,
  setExpectedAnnualRentIncrease,
  setHomePrice,
  setHowLongBeforeSelling,
  setIncomeTax,
  setInterestRate,
  setLoanTerm,
  setMonthlyRent,
  setShowFull
} from './rentOrBuySetters';

const initialState: RentOrBuyState = {
  monthlyRent: 800,
  expectedAnnualRentIncrease: 5,
  homePrice: 200000,
  downPayment: 10000,
  downPaymentPercent: 5,
  closingCostPercent: 1.5,
  interestRate: 6,
  loanTerm: LoanTerms.THIRTY,
  annualAppreciation: 3,
  howLongBeforeSelling: 10,
  currentAnnualInterest: 3,
  incomeTax: 28,
  showFull: false
};

export const LoanTermToNumber = {
  [LoanTerms.THIRTY]: 30,
  [LoanTerms.TWENTY]: 20,
  [LoanTerms.FIFTEEN]: 15
};

// eslint-disable-next-line abcsize/abcsize
function calculatorReducer(state: RentOrBuyState, action: RentOrBuyActions): RentOrBuyState {
  switch (action.type) {
    case RentOrBuyActionTypes.SET_MONTHLY_RENT:
      return setMonthlyRent(state, action.payload);
    case RentOrBuyActionTypes.SET_EXPECTED_ANNUAL_RENT_INCREASE:
      return setExpectedAnnualRentIncrease(state, action.payload);
    case RentOrBuyActionTypes.SET_HOME_PRICE:
      return setHomePrice(state, action.payload);
    case RentOrBuyActionTypes.SET_DOWN_PAYMENT:
      return setDownPayment(state, action.payload);
    case RentOrBuyActionTypes.SET_DOWN_PAYMENT_PERCENT:
      return setDownPaymentPercent(state, action.payload);
    case RentOrBuyActionTypes.SET_CLOSING_COST_PERCENT:
      return setClosingCostPercent(state, action.payload);
    case RentOrBuyActionTypes.SET_INTEREST_RATE:
      return setInterestRate(state, action.payload);
    case RentOrBuyActionTypes.SET_LOAN_TERM:
      return setLoanTerm(state, action.payload);
    case RentOrBuyActionTypes.SET_ANNUAL_APPRECIATION:
      return setAnnualAppreciation(state, action.payload);
    case RentOrBuyActionTypes.SET_HOW_LONG_BEFORE_SELLING:
      return setHowLongBeforeSelling(state, action.payload);
    case RentOrBuyActionTypes.SET_CURRENT_ANNUAL_INTEREST:
      return setCurrentAnnualInterest(state, action.payload);
    case RentOrBuyActionTypes.SET_INCOME_TAX:
      return setIncomeTax(state, action.payload);
    case RentOrBuyActionTypes.SET_SHOW_FULL:
      return setShowFull(state, action.payload);
    case RentOrBuyActionTypes.SET_DEFAULTS:
      return setDefaults(state, initialState);
    default:
      throw new Error(`Unhandled action type`);
  }
}

const RentOrBuyContext = createContext<RentOrBuyContextType>({
  state: initialState,
  dispatch: () => null,
  getCurrentValueOfHome: () => 0,
  getTotalOwedToBank: () => 0,
  getEquityOnHome: () => 0,
  getNetCostOfBuying: () => 0,
  getNetCostOfRenting: () => 0,
  getBenefitOfBuying: () => 0
});

const useComparisonResult = (state: RentOrBuyState) => {
  const result = compareBuyOrRent({
    monthlyRent: state.monthlyRent,
    purchasePrice: state.homePrice,
    downPayment: state.downPaymentPercent,
    interestRate: state.interestRate,
    termInYears: LoanTermToNumber[state.loanTerm],
    howLongBeforeSelling: state.howLongBeforeSelling,
    incomeTaxRate: state.incomeTax,
    expectedAnnualRentIncrease: state.expectedAnnualRentIncrease,
    closingCosts: state.closingCostPercent,
    annualAppreciation: state.annualAppreciation
  });

  let getCurrentValueOfHome = () => 0;
  let getTotalOwedToBank = () => 0;
  let getEquityOnHome = () => 0;
  let getNetCostOfBuying = () => 0;
  let getNetCostOfRenting = () => 0;
  let getBenefitOfBuying = () => 0;

  if (!('error' in result)) {
    const {
      currentValueOfHome,
      totalOwedToBank,
      equityOnHome,
      netCostOfBuying,
      netCostOfRenting,
      benefitOfBuying
    } = result;
    getCurrentValueOfHome = () => currentValueOfHome;
    getTotalOwedToBank = () => totalOwedToBank;
    getEquityOnHome = () => equityOnHome;
    getNetCostOfBuying = () => netCostOfBuying;
    getNetCostOfRenting = () => netCostOfRenting;
    getBenefitOfBuying = () => benefitOfBuying;
  } else {
    console.log('calculation error: ', result.error);
  }
  return {
    getCurrentValueOfHome,
    getTotalOwedToBank,
    getEquityOnHome,
    getNetCostOfBuying,
    getNetCostOfRenting,
    getBenefitOfBuying
  };
};
export function RentOrBuyProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  const {
    getCurrentValueOfHome,
    getTotalOwedToBank,
    getEquityOnHome,
    getNetCostOfBuying,
    getNetCostOfRenting,
    getBenefitOfBuying
  } = useComparisonResult(state);

  const value = {
    state,
    dispatch,
    getCurrentValueOfHome,
    getTotalOwedToBank,
    getEquityOnHome,
    getNetCostOfBuying,
    getNetCostOfRenting,
    getBenefitOfBuying
  };

  return <RentOrBuyContext.Provider value={value}>{children}</RentOrBuyContext.Provider>;
}

export function useRentOrBuy() {
  const context = useContext(RentOrBuyContext);
  if (context === undefined) {
    throw new Error('useRentOrBuy must be used within a RentOrBuyProvider');
  }
  return context;
}
